/**
 * Shared Firebase Functions callable helpers.
 *
 * All Cloud Functions are in europe-west3 unless otherwise noted.
 * Use these instead of raw fetch() calls so auth tokens are automatically
 * propagated and region handling is consistent.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west3');

// ─── User Management ─────────────────────────────────────────────────────────
export const requestJoinSchool = httpsCallable(functions, 'requestJoinSchool');
export const approveUserRegistration = httpsCallable(functions, 'approveUserRegistration');
export const listSchoolsForRegistration = httpsCallable(functions, 'listSchoolsForRegistration');
export const removeStaffFromSchool = httpsCallable(functions, 'removeStaffFromSchool');

// ─── Progress Tracking ───────────────────────────────────────────────────────
export const getStudentProgress = httpsCallable(functions, 'getStudentProgress');
export const updateTargetProgress = httpsCallable(functions, 'updateTargetProgress');
export const suggestTermlyTargets = httpsCallable(functions, 'suggestTermlyTargets');
export const generateGraduatedResponseReport = httpsCallable(functions, 'generateGraduatedResponseReport');

// ─── Group Teaching Plans ────────────────────────────────────────────────────
export const generateGroupTeachingPlan = httpsCallable(functions, 'generateGroupTeachingPlan');

// ─── Curriculum & Term Plans ─────────────────────────────────────────────────
export const generateCurriculum = httpsCallable(functions, 'generateCurriculum');
export const generateGroupCurriculum = httpsCallable(functions, 'generateGroupCurriculum');
export const generateTermPlan = httpsCallable(functions, 'generateTermPlan');

// ─── EHCP ────────────────────────────────────────────────────────────────────
export const uploadEHCP = httpsCallable(functions, 'uploadEHCP');
export const uploadSupportingReport = httpsCallable(functions, 'uploadSupportingReport');
export const analyzeEHCP = httpsCallable(functions, 'analyzeEHCP');
export const extractEHCPSectionE = httpsCallable(functions, 'extractEHCPSectionE');

// ─── Interventions ───────────────────────────────────────────────────────────
export const suggestInterventionGroups = httpsCallable(functions, 'suggestInterventionGroups');
export const suggestInterventionTargets = httpsCallable(functions, 'suggestInterventionTargets');
export const generateEHCNAContent = httpsCallable(functions, 'generateEHCNAContent');

// ─── V2 Insights / Command Center ────────────────────────────────────────────
export const getSencoCommandCenterV2 = httpsCallable(functions, 'getSencoCommandCenterV2');
export const getSchoolCohortInsightsV2 = httpsCallable(functions, 'getSchoolCohortInsightsV2');
export const detectMissingStudentDataV2 = httpsCallable(functions, 'detectMissingStudentDataV2');
export const suggestTargetsV2 = httpsCallable(functions, 'suggestTargetsV2');

// ─── V2 Profile / Document Pipeline ──────────────────────────────────────────
export const synthesizeStudentProfileV2 = httpsCallable(functions, 'synthesizeStudentProfileV2');
export const assignStudentTierV2 = httpsCallable(functions, 'assignStudentTierV2');
export const generateOnePageProfileV2 = httpsCallable(functions, 'generateOnePageProfileV2');
export const enqueueDocumentJobV2 = httpsCallable(functions, 'enqueueDocumentJobV2');
export const saveDocumentExtractionCorrectionsV2 = httpsCallable(functions, 'saveDocumentExtractionCorrectionsV2');
export const processDocumentJobV2 = httpsCallable(functions, 'processDocumentJobV2');
export const getStudentEvidenceBundleV2 = httpsCallable(functions, 'getStudentEvidenceBundleV2');
export const deleteTherapistReportV2 = httpsCallable(functions, 'deleteTherapistReportV2');
export const submitSupportPlanDraftV2 = httpsCallable(functions, 'submitSupportPlanDraftV2');
export const reviewSupportPlanV2 = httpsCallable(functions, 'reviewSupportPlanV2');
export const getTeacherSupportPlanV2 = httpsCallable(functions, 'getTeacherSupportPlanV2');
export const addProfessionalRecommendationToWaitlist = httpsCallable(functions, 'addProfessionalRecommendationToWaitlist');
export const dismissProfessionalRecommendation = httpsCallable(functions, 'dismissProfessionalRecommendation');
export const createApdrEvidenceSnapshot = httpsCallable(functions, 'createApdrEvidenceSnapshot');
export const listApdrEvidenceSnapshots = httpsCallable(functions, 'listApdrEvidenceSnapshots');
export const getApdrTargetReviewQueueForStudent = httpsCallable(functions, 'getApdrTargetReviewQueueForStudent');
export const approveApdrTargetReviewQueueItem = httpsCallable(functions, 'approveApdrTargetReviewQueueItem');
export const generateReviewPackV2 = httpsCallable(functions, 'generateReviewPackV2');
export const listReviewPacksForStudentV2 = httpsCallable(functions, 'listReviewPacksForStudentV2');

// ─── GDPR / Data Management ──────────────────────────────────────────────────
export const deleteStudentData = httpsCallable(functions, 'deleteStudentData');
export const exportStudentData = httpsCallable(functions, 'exportStudentData');
export const getStudentAuditTrail = httpsCallable(functions, 'getStudentAuditTrail');

// ─── Individual Support Plan (ISP) ───────────────────────────────────────────
export const exportIspPdf = httpsCallable(functions, 'exportIspPdf');
export const exportEhcnaPdf = httpsCallable(functions, 'exportEhcnaPdf');
export const suggestApdrCycleContent = httpsCallable(functions, 'suggestApdrCycleContent');

// ─── EHCP Section F provisioning & intervention waitlist ─────────────────────
export const generateEhcpProvisionProposals = httpsCallable(functions, 'generateEhcpProvisionProposals');
export const getSectionFProvisionStatus = httpsCallable(functions, 'getSectionFProvisionStatus');
export const acceptEhcpProvisionProposal = httpsCallable(functions, 'acceptEhcpProvisionProposal');
export const dismissEhcpProvisionProposal = httpsCallable(functions, 'dismissEhcpProvisionProposal');
export const listInterventionWaitlist = httpsCallable(functions, 'listInterventionWaitlist');
export const scanInterventionSwapSuggestions = httpsCallable(functions, 'scanInterventionSwapSuggestions');
export const acceptInterventionSwapSuggestion = httpsCallable(functions, 'acceptInterventionSwapSuggestion');
export const dismissInterventionSwapSuggestion = httpsCallable(functions, 'dismissInterventionSwapSuggestion');
export const getSencoProvisionOverview = httpsCallable(functions, 'getSencoProvisionOverview');
export const getSencoCohortAnalytics = httpsCallable(functions, 'getSencoCohortAnalytics');

// ─── Assessments ─────────────────────────────────────────────────────────────
export const listAssessmentTemplates = httpsCallable(functions, 'listAssessmentTemplatesFunc');
export const saveSchoolAssessment = httpsCallable(functions, 'saveSchoolAssessmentFunc');
export const generateAssessmentPDF = httpsCallable(functions, 'generateAssessmentPDFFunc');

// ─── Downloadable Resources ──────────────────────────────────────────────────
export const generateWorksheet = httpsCallable(functions, 'generateWorksheetFunc');
export const generateProgressSheet = httpsCallable(functions, 'generateProgressSheetFunc');
export const generateSensoryStory = httpsCallable(functions, 'generateSensoryStoryFunc');
export const generateVisualSupport = httpsCallable(functions, 'generateVisualSupportFunc');
